import React, { useState } from 'react';
import { FaExclamationTriangle } from 'react-icons/fa';

interface ErrorMessageProps {
  title?: string;
  message: string;
  suggestion?: string;
}

export const ErrorMessage: React.FC<ErrorMessageProps> = ({ 
  title = 'Access Error', 
  message, 
  suggestion = 'Please use a valid URL with the required parameters.' 
}) => {
  const [clientId, setClientId] = useState('');
  const [uinIds, setUinIds] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!clientId.trim()) {
      setError('Client ID is required');
      return;
    }
    
    setError('');
    
    // Construct URL with parameters - point directly to settings page
    const origin = window.location.origin;
    let redirectUrl = `${origin}/settings?client_id=${encodeURIComponent(clientId)}`;
    
    if (uinIds.trim()) {
      redirectUrl += `&uin_ids=${encodeURIComponent(uinIds)}`;
    }
    
    // Redirect to the new URL
    window.location.href = redirectUrl;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="bg-red-600 p-6 flex justify-center">
          <div className="text-white text-6xl">
            <>{FaExclamationTriangle({})}</>
          </div>
        </div>
        <div className="p-6 sm:p-8">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">{title}</h1>
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
            <p className="text-red-700">{message}</p>
          </div>
          <p className="text-gray-600 mb-6">{suggestion}</p>
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
            <h3 className="font-medium text-blue-700 mb-2">Example Valid URL:</h3>
            <code className="block bg-gray-100 p-2 rounded text-sm overflow-x-auto">
              https://domain.com/settings?client_id=YOUR_CLIENT_ID
            </code>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="clientId" className="block text-sm font-medium text-gray-700 mb-1">
                Client ID <span className="text-red-500">*</span>
              </label>
              <input
                id="clientId"
                type="text"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter your client ID"
                required
              />
            </div>
            
            <div>
              <label htmlFor="uinIds" className="block text-sm font-medium text-gray-700 mb-1">
                UIN IDs (Optional, comma-separated)
              </label>
              <input
                id="uinIds"
                type="text"
                value={uinIds}
                onChange={(e) => setUinIds(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g. 123,456,789"
              />
              <p className="mt-1 text-xs text-gray-500">Multiple UIN IDs should be separated by commas</p>
            </div>
            
            {error && (
              <div className="bg-red-50 border-l-4 border-red-500 p-3">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}
            
            <button 
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition duration-200"
            >
              Access Application
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ErrorMessage; 